import { createContext, useContext } from "react";

/**
 * Whether the interactive controls inside a cell (row actions, links) are in
 * the Tab order. The grid provides `true` for the focused row's cells in row
 * mode and for the active cell in cell navigation; everywhere else controls
 * stay at `tabIndex={-1}` so the grid remains one tab stop.
 */
export const CellControlsTabbableContext = createContext(false);

/**
 * Spread on a cell control whose border shows at rest (a pill, an input, a
 * bordered picker). Cell triggers that look like plain text until hovered
 * shift left by their own padding so their text lines up with the column
 * header; a control that always draws its border keeps the cell padding
 * instead, so the border never touches the cell edge or a pinned column's
 * shadow. The grid cancels the shift (`bordered-control` in `_reveal.scss`).
 */
export const DATA_GRID_BORDERED_CONTROL_PROPS = { "data-dt-bordered": "" };

/** Cell content that handles its own clicks and keys. */
export const CELL_CONTROL_SELECTOR =
    "button, a[href], input, select, textarea, [role='checkbox'], [role='button'], [role='menuitem'], [data-dt-interactive]";

/**
 * Whether `target` sits in an element matching `selector` inside
 * `container`. Only matches inside count: the grid may itself sit inside a
 * control (a drop zone button).
 */
export const isInsideMatching = ({
    target,
    container,
    selector,
}: {
    target: EventTarget | null;
    container: Element;
    selector: string;
}) => {
    const match = target instanceof Element ? target.closest(selector) : null;
    return match !== null && container.contains(match);
};

/**
 * Whether the target is cell content that handles its own clicks and keys
 * (`CELL_CONTROL_SELECTOR`) inside `container`.
 */
export const isInsideInteractiveCellContent = (
    target: EventTarget | null,
    container: Element,
) => isInsideMatching({ target, container, selector: CELL_CONTROL_SELECTOR });

/** `tabIndex` for a control inside a cell: 0 when its cell allows tabbing in. */
export const useCellControlTabIndex = () =>
    useContext(CellControlsTabbableContext) ? 0 : -1;
